<template>
    <div class="top-nav">
        <div class="nav-left">
            <h1 class="title">HTcard</h1>
        </div>
        <div class="nav-right">
            <!-- 未登入狀態 -->
            <div v-if="!isLogin" class="login-section">
                <button class="login-btn" @click="loginTMID">
                    {{ $t('登入') }}
                </button>
            </div>

            <!-- 已登入狀態 -->
            <div v-else class="user-section">
                <div class="user-info" @click="toggleUserMenu">
                    <img v-if="profile.picture" :src="profile.picture" class="avatar" alt="avatar">
                    <div v-else class="avatar-placeholder">{{ profile.name?.charAt(0) }}</div>
                    <span class="user-name">{{ profile.name }}</span>
                </div>

                <!-- 用戶選單 -->
                <div v-show="showUserMenu" class="user-menu">
                    <div class="menu-item">{{ profile.name }}</div>
                    <div class="menu-divider"></div>
                    <div class="menu-item menu-action" @click="logout">
                        {{ $t('登出') }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { Message } from 'view-ui-plus'
import api from '@/api'
import jwt_decode from 'jwt-decode'
import { useMainStore } from '@/stores/mainStore'

const { t } = useI18n()
const store = useMainStore()

// 響應式狀態
const isLogin = ref(false)
const showUserMenu = ref(false)
const profile = reactive({
    name: "",
    teammodelId: "",
    picture: ""
})

const tokenRefreshInterval = ref(null)

// 定義事件
// eslint-disable-next-line no-undef
const emit = defineEmits(['login-success', 'logout-success'])

// 登入方法
const loginTMID = () => {
    api.main.loginTmdID()
}

// 登出方法
const logout = async () => {
    showUserMenu.value = false
    if (isLogin.value) {
        try {
            await api.main.logout()
            Message.info(t('登出') + '成功')
            isLogin.value = false
            store.setIsLogIn(false)

            // 清除本地儲存
            localStorage.removeItem('access_token')
            localStorage.removeItem('id_token')
            localStorage.removeItem('expires_in')
            localStorage.removeItem('profile')
            localStorage.removeItem('auth_token')
            localStorage.removeItem('auth_token_expiry')
            sessionStorage.clear()

            // 重置用戶資料
            profile.name = ""
            profile.teammodelId = ""
            profile.picture = ""

            emit('logout-success')
        } catch (error) {
            console.error('Logout error:', error)
            Message.error(t('登出') + '失敗')
        }
    }
}

// 切換用戶選單
const toggleUserMenu = () => {
    showUserMenu.value = !showUserMenu.value
}

// 初始化認證
const initializeAuth = async () => {
    try {
        const res = await api.main.getTokenUnlogin()
        const exp = jwt_decode(res.auth_token).exp
        localStorage.setItem("auth_token", res.auth_token)
        localStorage.setItem("auth_token_expiry", exp)
    } catch (error) {
        console.error("Error fetching auth token:", error)
    }
}

// 檢查自動登入
const checkAutoLogin = () => {
    const savedProfile = localStorage.getItem("profile")
    const savedToken = localStorage.getItem("access_token")

    if (savedProfile && savedToken) {
        try {
            const profileData = JSON.parse(savedProfile)
            isLogin.value = true
            profile.name = profileData.name
            profile.teammodelId = profileData.id || profileData.teammodelId
            profile.picture = profileData.picture
            store.setIsLogIn(true)
        } catch (error) {
            console.error("Auto login error:", error)
        }
    }
}

// 處理 OAuth 回調
const handleOAuthCallback = async () => {
    const url = new URL(window.location.href)
    const code = url.searchParams.get("code")

    if (code) {
        try {
            const res = await api.main.getToken()
            localStorage.setItem("id_token", res.id_token)
            localStorage.setItem("access_token", res.access_token)
            localStorage.setItem("expires_in", res.expires_in)

            const decoded = jwt_decode(res.id_token)

            isLogin.value = true
            store.setIsLogIn(true)
            profile.name = decoded.name
            profile.teammodelId = decoded.sub
            profile.picture = decoded.picture

            localStorage.setItem("profile", JSON.stringify({
                name: profile.name,
                id: profile.teammodelId,
                picture: profile.picture,
            }))

            // 清除 URL 中的 code 參數
            const newUrl = new URL(window.location.href)
            newUrl.searchParams.delete('code')
            newUrl.searchParams.delete('state')
            window.history.replaceState({}, document.title, newUrl.toString())

            Message.success('登入成功！')
            emit('login-success', {
                name: profile.name,
                id: profile.teammodelId,
                picture: profile.picture
            })
        } catch (error) {
            console.error("OAuth callback error:", error)
            Message.error('登入失敗')
        }
    }
}

// Token 刷新間隔
const startTokenRefreshInterval = () => {
    tokenRefreshInterval.value = setInterval(async () => {
        const authExpiry = localStorage.getItem("auth_token_expiry");
        const now = Date.now();
        const refreshThreshold = 10 * 1000;

        if (authExpiry) {
            const exp = parseInt(authExpiry) * 1000;
            if (exp - now < refreshThreshold) {
                localStorage.removeItem("auth_token");
                localStorage.removeItem("auth_token_expiry");
                await initializeAuth();
            }
        }
    }, 60 * 1000);
}

// 點擊外部關閉選單
const handleClickOutside = (event) => {
    if (!event.target.closest('.user-section')) {
        showUserMenu.value = false
    }
}

// 暴露給父組件的方法和狀態
// eslint-disable-next-line no-undef
defineExpose({
    isLogin,
    profile
})

// 組件掛載時執行
onMounted(async () => {
    await initializeAuth()
    checkAutoLogin()
    startTokenRefreshInterval()
    await handleOAuthCallback()
    document.addEventListener('click', handleClickOutside)
})

// 清理
onBeforeUnmount(() => {
    if (tokenRefreshInterval.value) {
        clearInterval(tokenRefreshInterval.value)
    }
    document.removeEventListener('click', handleClickOutside)
})
</script>

<style lang="less" scoped>
@import "../../assets/HTwbCardCSS/HTwbCardVal.less";

.top-nav {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 2rem;
    background: @bg-color-light;
    border-bottom: 1px solid @border-color-light;
    height: @navbar-height;

    .nav-left {
        .title {
            color: @text-color;
            font-size: 1.5rem;
            font-weight: bold;
            margin: 0;
        }
    }

    .nav-right {
        position: relative;
        
        .login-section {
            .login-btn {
                background: @primary-color;
                border: 1px solid @primary-color;
                color: @text-color-white;
                padding: 0.2rem 0.6rem;
                border-radius: @border-radius;
                cursor: pointer;
                transition: all 0.2s ease;
                font-size: 0.8rem;
                
                &:hover {
                    background: @primary-color-hover;
                    border-color: @primary-color-hover;
                }
            }
        }
        
        .user-section {
            .user-info {
                display: flex;
                align-items: center;
                gap: 0.3rem;
                cursor: pointer;
                padding: 10px;
                border-radius: @border-radius;
                transition: background 0.2s ease;
                
                &:hover {
                    background: @bg-color-hover;
                }
                
                .avatar {
                    width: @avatar-size;
                    height: @avatar-size;
                    border-radius: 50%;
                    border: 1px solid @border-color-gray;
                    object-fit: cover;
                }
                
                .avatar-placeholder {
                    width: @avatar-size;
                    height: @avatar-size;
                    border-radius: 50%;
                    background: @secondary-color;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: @text-color-white;
                    font-weight: bold;
                    font-size: 0.7rem;
                }
                
                .user-name {
                    color: @text-color;
                    font-weight: 500;
                    font-size: 1rem;
                }
            }
            
            .user-menu {
                position: absolute;
                top: 45px;
                right: 0px;
                background-color: @bg-color-white;
                color: @text-color;
                border: 1px solid @border-color-menu;
                border-radius: @border-radius-small;
                padding: 5px 0px;
                z-index: 1000;
                min-width: 100px;
                width: auto;
                box-shadow: 2px 2px 2px @shadow-color;

                .menu-item {
                    padding: 0.3rem 0.8rem;
                    font-size: 0.9rem;
                    cursor: default;
                    
                    &:first-child {
                        font-weight: 500;
                        color: @text-color;
                    }

                    &.menu-action {
                        cursor: pointer;
                        transition: background-color 0.2s;

                        &:hover {
                            background: @bg-color-hover-light;
                        }
                    }
                }

                .menu-divider {
                    height: 1px;
                    background: @divider-color;
                    margin: 3px 0;
                }
            }
        }
    }
}
</style>